/**
 * Stores Dual-Write Test Suite
 *
 * This utility automatically creates stores with various configurations
 * and verifies that Supabase and Firestore are in sync.
 *
 * Usage:
 *   import { runStoresDualWriteTests } from './utils/storesDualWriteTest';
 *   await runStoresDualWriteTests(db, testConfig);
 */

import { sanitizeForFirestore } from '../global/Firebase/productSanitize'
import {
  saveNewStore,
  updateStore,
  deleteStore,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { storesApi } from '../global/API/typedApiClient';
import { getDefaultStore } from 'jotai';
import {
  organizationAtom,
  dbAtom,
  userIdAtom,
  storesDictAtom,
  productsDictAtom,
  constantsAtom,
} from '../global/AtomsProvider';

// Test result tracking
const testResults = {
  passed: [],
  failed: [],
  skipped: [],
};

/**
 * All test variations to run
 */
export const STORE_TEST_VARIATIONS = [
  // Basic CRUD (ST1-ST2)
  { id: 'ST1', category: 'basic', name: 'Create with required fields only' },
  { id: 'ST2', category: 'basic', name: 'Create with all fields' },

  // Update (ST3)
  { id: 'ST3', category: 'lifecycle', name: 'Update store', lifecycleAction: 'update' },

  // Delete (ST4)
  { id: 'ST4', category: 'lifecycle', name: 'Delete store', lifecycleAction: 'delete' },
];

/**
 * Generate test store data for a specific variation
 */
export function generateTestStore(variation, testData) {
  const { organizationId } = testData;

  // Base store data
  const timestamp = Date.now();

  const baseStore = {
    name: `Test Store ${variation.id} - ${timestamp}`,
    timezone: {
      value: 'Asia/Almaty',
      name: '(UTC+05:00) Almaty',
      offset: 5,
      abbreviation: 'ALMT',
    },
    businessDayCutoffTime: '04:00:00',
    inventoryCostingMethod: 'FIFO',
    showOnTop: false,
    // Optional fields are omitted instead of null to avoid API validation errors
  };

  // Apply variation
  switch (variation.category) {
    case 'basic':
      return applyBasicVariation(baseStore, variation, testData);
    case 'lifecycle':
      return applyLifecycleVariation(baseStore, variation, testData);
    default:
      return baseStore;
  }
}

function applyBasicVariation(store, variation, testData) {
  switch (variation.id) {
    case 'ST1': // Required fields only
      store.name = `Test Basic Store ${Date.now()}`;
      break;

    case 'ST2': // All fields
      store.name = `Test Full Store ${Date.now()}`;
      store.address = '123 Test Street, Test City';
      store.autoCloseShiftTime = { hour: 23, minute: 0 };
      store.showOnTop = true;
      break;
  }

  return store;
}

function applyLifecycleVariation(store, variation, testData) {
  // Start with a basic store that can be modified
  store.name = `Test Lifecycle Store ${variation.id} - ${Date.now()}`;

  // Store lifecycle action metadata
  store._lifecycleAction = variation.lifecycleAction;
  store._lifecycleData = {};

  switch (variation.id) {
    case 'ST3': // Update
      store._lifecycleData = {
        updateFields: true,
        updateInfo: {
          name: `Updated Store Name ${Date.now()}`,
          address: 'Updated Address 456',
          showOnTop: true,
        },
      };
      break;

    case 'ST4': // Delete
      store._lifecycleData = {
        deleteStore: true,
      };
      break;
  }

  return store;
}

/**
 * Execute lifecycle action after store creation
 */
async function executeLifecycleAction(db, storeRef, storeData, testData) {
  const action = storeData._lifecycleAction;
  const data = storeData._lifecycleData;

  if (!action || !data) return { actionPerformed: null };

  // Handle skip case
  if (data.skip) {
    return { actionPerformed: 'skip', skipReason: data.skipReason };
  }

  // Wait a bit for initial sync
  await new Promise(resolve => setTimeout(resolve, 1000));

  try {
    if (data.updateFields) {
      // Update store via updateStore
      await updateStore(storeRef, data.updateInfo);
      return { actionPerformed: 'updateFields' };
    }

    if (data.deleteStore) {
      // Delete store
      await deleteStore(storeRef);
      return { actionPerformed: 'delete' };
    }
  } catch (error) {
    console.error('Lifecycle action failed:', error.message);
    return { actionPerformed: action, error: error.message };
  }

  return { actionPerformed: null };
}

/**
 * Run a single test variation
 */
async function runSingleTest(db, variation, testData, options = {}) {
  const { cleanup = true, delayMs = 5000 } = options;

  console.log(`\n--- Running test ${variation.id}: ${variation.name} ---`);

  try {
    // Generate test store
    const storeData = generateTestStore(variation, testData);

    // Check if test should be skipped
    if (storeData._lifecycleData?.skip) {
      console.log(`SKIP: ${variation.name} - ${storeData._lifecycleData.skipReason}`);
      testResults.skipped.push({ variation, reason: storeData._lifecycleData.skipReason });
      return null;
    }

    console.log(`Creating store...`);

    // Create store via frontend code (this triggers dual-write)
    // saveNewStore expects (db, info, allFlatProducts, constants, copySettingsFrom)
    const result = await saveNewStore(db, storeData, testData.products, testData.constants, null);

    // Get the firestore ID from the result
    // saveNewStore returns the batch commit result, but we need to find the store by name
    // Wait a bit for the creation
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Query the stores collection to find the newly created store
    const storesSnapshot = await db.collection('stores').where('name', '==', storeData.name).get();

    if (storesSnapshot.empty) {
      throw new Error(`Could not find created store with name: ${storeData.name}`);
    }

    const storeDoc = storesSnapshot.docs[0];
    const firestoreId = storeDoc.id;
    const storeRef = db.collection('stores').doc(firestoreId);

    console.log(`Created store: ${firestoreId}`);

    // Wait for Supabase write to complete (CRITICAL: 5 second wait)
    await new Promise(resolve => setTimeout(resolve, delayMs));

    // For lifecycle tests, perform the action after initial creation
    let lifecycleResult = null;
    if (variation.category === 'lifecycle') {
      console.log(`Performing lifecycle action: ${variation.lifecycleAction}`);
      lifecycleResult = await executeLifecycleAction(db, storeRef, storeData, testData);
      console.log(`Lifecycle action result:`, lifecycleResult);

      // Handle skipped lifecycle tests
      if (lifecycleResult?.actionPerformed === 'skip') {
        console.log(`SKIP: ${variation.name} - ${lifecycleResult.skipReason}`);
        testResults.skipped.push({ variation, reason: lifecycleResult.skipReason });
        // Cleanup the created store
        if (cleanup) {
          try {
            await deleteStore(storeRef);
          } catch (e) { /* ignore */ }
        }
        return null;
      }

      // Wait for the update to sync (CRITICAL: 5 second wait)
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }

    // Compare using the API
    let comparisonResult;

    // Special case: if store was deleted, verify it's gone from both DBs
    if (lifecycleResult?.actionPerformed === 'delete') {
      comparisonResult = await verifyDeletion(firestoreId, storeRef, db);
    } else {
      comparisonResult = await compareViaApi(firestoreId, storeRef, db, storeData);
    }

    // Log result
    if (comparisonResult.matches) {
      console.log(`PASS: ${variation.name}`);
      testResults.passed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    } else {
      console.log(`FAIL: ${variation.name}`);
      console.log('Differences:', JSON.stringify(sanitizeForFirestore(comparisonResult.differences), null, 2));
      testResults.failed.push({
        variation,
        firestoreId,
        result: comparisonResult,
        lifecycleResult,
      });
    }

    // Cleanup - delete the test store (skip if already deleted)
    if (cleanup && lifecycleResult?.actionPerformed !== 'delete') {
      try {
        const storeDocCheck = await storeRef.get();
        if (storeDocCheck.exists) {
          await deleteStore(storeRef);
          console.log(`Cleaned up store: ${firestoreId}`);
        }
      } catch (cleanupError) {
        console.warn(`Failed to cleanup store ${firestoreId}:`, cleanupError.message);
      }
    }

    return comparisonResult;
  } catch (error) {
    console.error(`ERROR in test ${variation.id}:`, error.message);
    console.error(`Stack trace:`, error.stack);
    testResults.failed.push({
      variation,
      error: error.message,
      stack: error.stack,
    });
    return null;
  }
}

/**
 * Compare store data via backend API
 */
async function compareViaApi(firestoreId, storeRef, db, originalStoreData) {
  try {
    const response = await api.get(`/stores/compare/${firestoreId}`);
    const apiResult = response.data;

    // Convert API result to our comparison format
    if (!apiResult.exists) {
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: true,
        matches: false,
        differences: [{
          field: 'store',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    // Get Firestore data for comparison
    let firestoreData = null;
    try {
      const doc = await storeRef.get();
      if (doc.exists) {
        firestoreData = { id: doc.id, ...doc.data() };
      }
    } catch (error) {
      console.warn(`Could not fetch from Firestore: ${error.message}`);
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare fields
    const fieldsToCompare = [
      { supabase: 'name', firestore: 'name' },
      { supabase: 'address', firestore: 'address' },
      { supabase: 'timezone', firestore: 'timezone', transform: 'timezone' },
      // business_day_cutoff_time is intentionally NOT compared: on Supabase it is a
      // server-derived mirror of auto_close_shift_time (trg_stores_sync_cutoff, ATL-658),
      // so it will legitimately differ from the raw Firestore businessDayCutoffTime.
      // autoCloseShiftTime is the authoritative source and is what should match.
      { supabase: 'inventory_costing_method', firestore: 'inventoryCostingMethod' },
    ];

    for (const field of fieldsToCompare) {
      let supabaseValue = supabaseData?.[field.supabase];
      let firestoreValue = firestoreData?.[field.firestore];

      // Handle timezone transform (Firestore has object, Supabase has string)
      if (field.transform === 'timezone' && firestoreValue && typeof firestoreValue === 'object') {
        firestoreValue = firestoreValue.value || firestoreValue;
      }

      // Normalize values
      supabaseValue = normalizeValue(supabaseValue);
      firestoreValue = normalizeValue(firestoreValue);

      if (!valuesMatch(supabaseValue, firestoreValue)) {
        differences.push({
          field: field.supabase,
          supabaseValue,
          firestoreValue,
          severity: 'critical',
        });
      }
    }

    return {
      firestoreId,
      foundInSupabase: true,
      foundInFirestore: !!firestoreData,
      matches: differences.length === 0,
      differences,
      warnings: [],
      supabaseData,
      firestoreData,
    };
  } catch (error) {
    console.warn('API comparison failed:', error.message);
    return {
      firestoreId,
      foundInSupabase: false,
      foundInFirestore: true,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: error.message,
        firestoreValue: 'exists',
        severity: 'critical',
      }],
      warnings: [error.message],
    };
  }
}

/**
 * Verify that a store has been deleted from both databases
 */
async function verifyDeletion(firestoreId, storeRef, db) {
  const maxRetries = 15;
  const retryDelayMs = 2000;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    const differences = [];
    let firestoreExists = false;
    let supabaseExists = false;

    // Check Firestore
    try {
      const firestoreDoc = await storeRef.get();
      firestoreExists = firestoreDoc.exists;
    } catch (error) {
      firestoreExists = false;
    }

    // Check Supabase via compare endpoint
    try {
      const response = await api.get(`/stores/compare/${firestoreId}`);
      supabaseExists = response.data?.exists || false;
    } catch (error) {
      // 404 or other error means not found
      supabaseExists = false;
    }

    // Both deleted - success!
    if (!firestoreExists && !supabaseExists) {
      console.log(`Delete verified on attempt ${attempt}: both databases show deleted`);
      return {
        firestoreId,
        foundInSupabase: false,
        foundInFirestore: false,
        matches: true,
        differences: [],
        warnings: [],
      };
    }

    // Build differences for logging
    if (firestoreExists) {
      differences.push({
        field: 'firestore_deletion',
        supabaseValue: 'deleted',
        firestoreValue: 'still exists',
        severity: 'critical',
      });
    }
    if (supabaseExists) {
      differences.push({
        field: 'supabase_deletion',
        supabaseValue: 'still exists',
        firestoreValue: 'deleted',
        severity: 'critical',
      });
    }

    // If last attempt, return failure
    if (attempt === maxRetries) {
      console.log(`Delete verification failed after ${maxRetries} attempts`);
      return {
        firestoreId,
        foundInSupabase: supabaseExists,
        foundInFirestore: firestoreExists,
        matches: false,
        differences,
        warnings: [`Deletion not complete after ${maxRetries} retries`],
      };
    }

    // Wait and retry
    console.log(`Delete verification attempt ${attempt}/${maxRetries}: waiting...`);
    await new Promise(resolve => setTimeout(resolve, retryDelayMs));
  }

  return {
    firestoreId,
    foundInSupabase: true,
    foundInFirestore: true,
    matches: false,
    differences: [],
    warnings: ['Unexpected state in verifyDeletion'],
  };
}

function normalizeValue(value) {
  if (value === null || value === undefined) return null;
  if (value === '') return null;
  if (typeof value === 'number') return Number(value);
  if (typeof value === 'boolean') return value;
  return value;
}

function valuesMatch(a, b) {
  if ((a === null || a === undefined) && (b === null || b === undefined)) {
    return true;
  }
  if (a === null || a === undefined || b === null || b === undefined) {
    return false;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return Math.abs(a - b) < 0.01;
  }
  if (typeof a === 'boolean' || typeof b === 'boolean') {
    return Boolean(a) === Boolean(b);
  }
  return String(a) === String(b);
}

/**
 * Run all test variations
 */
export async function runStoresDualWriteTests(passedDb, options = {}) {
  const {
    variations = STORE_TEST_VARIATIONS,
    cleanup = true,
    delayMs = 5000,
    stopOnFailure = false,
  } = options;

  // Reset results
  testResults.passed = [];
  testResults.failed = [];
  testResults.skipped = [];

  // Get test data from Jotai store
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const userId = jotaiStore.get(userIdAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const constants = jotaiStore.get(constantsAtom);

  // Get db from atom if not passed
  const db = passedDb || jotaiStore.get(dbAtom);

  if (!db) {
    throw new Error('Database not initialized. Please wait for app to fully load.');
  }

  if (!organization || Object.keys(organization).length === 0) {
    throw new Error('Organization not found. Please ensure you are logged in.');
  }

  if (!constants || Object.keys(constants).length === 0) {
    throw new Error('Constants not loaded. Please wait for app to fully load.');
  }

  // Get first store for reference
  const storeId = Object.keys(storesDict || {})[0];
  const store = storesDict?.[storeId];

  // Flatten products for saveNewStore
  const flatProducts = productsDict ? Object.values(productsDict).flat() : [];

  const testData = {
    organizationId: organization.id,
    storeId,
    store,
    stores: storesDict,
    products: flatProducts,
    constants,
    uid: userId || 'test-uid',
  };

  console.log('\n========================================');
  console.log('STORES DUAL-WRITE TEST SUITE');
  console.log('========================================');
  console.log(`Organization: ${organization.name || organization.id}`);
  console.log(`Current Store: ${store?.name || storeId || 'None'}`);
  console.log(`Variations to test: ${variations.length}`);
  console.log('========================================\n');

  // Run each test
  for (const variation of variations) {
    const result = await runSingleTest(db, variation, testData, { cleanup, delayMs });

    if (stopOnFailure && result && !result.matches) {
      console.log('\nStopping due to failure (stopOnFailure=true)');
      break;
    }
  }

  // Print summary
  console.log('\n========================================');
  console.log('TEST SUMMARY');
  console.log('========================================');
  console.log(`Passed: ${testResults.passed.length}`);
  console.log(`Failed: ${testResults.failed.length}`);
  console.log(`Skipped: ${testResults.skipped.length}`);
  console.log('========================================\n');

  if (testResults.failed.length > 0) {
    console.log('FAILED TESTS:');
    testResults.failed.forEach(({ variation, result, error }) => {
      console.log(`  - ${variation.id}: ${variation.name}`);
      if (error) {
        console.log(`    Error: ${error}`);
      } else if (result?.differences) {
        result.differences.forEach(diff => {
          console.log(`    ${diff.field}: Supabase=${JSON.stringify(sanitizeForFirestore(diff.supabaseValue))} vs Firestore=${JSON.stringify(sanitizeForFirestore(diff.firestoreValue))}`);
        });
      }
    });
  }

  return testResults;
}

/**
 * Quick smoke test - runs only the first 2 basic tests
 */
export async function runQuickStoresSmokeTest(passedDb, options = {}) {
  const quickVariations = STORE_TEST_VARIATIONS.filter(v =>
    v.id === 'ST1' || v.id === 'ST2'
  );

  return runStoresDualWriteTests(passedDb, {
    ...options,
    variations: quickVariations,
  });
}

export default {
  runStoresDualWriteTests,
  generateTestStore,
  STORE_TEST_VARIATIONS,
};
